import { GlossaryTermWithSector } from '../../services/glossaryService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faInfo } from '@fortawesome/free-solid-svg-icons';
import { Badge } from '../ui/Badge';

interface TermsTableProps {
  terms: GlossaryTermWithSector[];
  onEdit: (term: GlossaryTermWithSector) => void;
  onDelete: (termId: number) => void;
}

const priorityColors = {
  ALTA: 'bg-red-100 text-red-700 border-red-300',
  MEDIA: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  BAJA: 'bg-green-100 text-green-700 border-green-300',
};

const mcerlLevelColors = {
  B2: 'bg-blue-100 text-blue-700 border-blue-300',
  C1: 'bg-purple-100 text-purple-700 border-purple-300',
  C2: 'bg-pink-100 text-pink-700 border-pink-300',
};

export default function TermsTable({ terms, onEdit, onDelete }: TermsTableProps) {
  if (terms.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <FontAwesomeIcon icon={faInfo} className="text-gray-300 text-5xl mb-4" />
        <p className="text-gray-500 text-lg">No hay términos en este sector</p>
        <p className="text-gray-400 text-sm mt-2">
          Añade nuevos términos o importa desde un archivo
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b-2 border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Término
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Definición
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Prioridad
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Nivel MCERL
            </th>
            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {terms.map((term) => (
            <tr key={term.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-4">
                <div>
                  <p className="font-semibold text-gray-900">{term.term}</p>
                  {term.acronym && (
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-medium">Acrónimo:</span> {term.acronym}
                    </p>
                  )}
                  {term.synonyms && Array.isArray(term.synonyms) && term.synonyms.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-medium">Sinónimos:</span>{' '}
                      {term.synonyms.join(', ')}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-4 py-4">
                <div className="max-w-md">
                  <p className="text-sm text-gray-700 line-clamp-2">{term.definition}</p>
                  {term.simplifiedDefinition && (
                    <p className="text-xs text-blue-600 mt-2 line-clamp-2">
                      <span className="font-medium">Simple:</span> {term.simplifiedDefinition}
                    </p>
                  )}
                  {term.examples && Array.isArray(term.examples) && term.examples.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      <span className="font-medium">Ej:</span> {term.examples[0]}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-4 py-4 text-center">
                <Badge className={`${priorityColors[term.priority]} border text-xs font-semibold`}>
                  {term.priority}
                </Badge>
              </td>
              <td className="px-4 py-4 text-center">
                <Badge className={`${mcerlLevelColors[term.mcerlLevel]} border text-xs font-semibold`}>
                  {term.mcerlLevel}
                </Badge>
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => onEdit(term)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar término"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => onDelete(term.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar término"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
